<template>
  <transition name="announcement-drop" @after-leave="handleAfterLeave">
    <aside
      v-if="isVisible && currentNotice"
      :key="currentNotice.id"
      class="notice-bar"
      :class="[isCheer ? 'is-cheer' : 'is-notice', { 'is-paused': isPaused }]"
      role="status"
      aria-live="polite"
      aria-atomic="true"
      @mouseenter="isHovering = true"
      @mouseleave="isHovering = false"
    >
      <div v-if="isCheer" class="celebration-layer" aria-hidden="true">
        <div class="celebration-spotlights">
          <span v-for="beam in 7" :key="`beam-${beam}`" class="celebration-beam" />
        </div>
        <div class="celebration-confetti">
          <span v-for="piece in 36" :key="`piece-${piece}`" class="celebration-piece" />
        </div>
        <div class="celebration-sparks">
          <span v-for="spark in 10" :key="`spark-${spark}`" class="celebration-spark" />
        </div>
      </div>

      <div class="notice-identity">
        <span class="notice-icon" aria-hidden="true">
          <i :class="isCheer ? 'el-icon-trophy' : 'el-icon-bell'" />
        </span>
      </div>

      <div ref="track" class="notice-track">
        <div
          ref="message"
          class="notice-message"
          :class="{ 'is-running': animationReady }"
          :style="animationStyle"
          @animationend="handleAnimationEnd"
        >
          {{ currentNotice.message }}
        </div>
      </div>

      <button
        type="button"
        class="notice-close"
        aria-label="关闭当前公告"
        title="关闭当前公告"
        @click="closeNotification"
      >
        <i class="el-icon-close" aria-hidden="true" />
      </button>
    </aside>
  </transition>
</template>

<script>
export default {
  name: 'NoticeBar',
  data() {
    return {
      isVisible: false,
      isPageHidden: false,
      isHovering: false,
      messageQueue: [],
      currentNotice: null,
      nextNoticeId: 1,
      animationReady: false,
      animationStart: 0,
      animationDuration: 16,
      animationFrame: null,
      resizeTimer: null
    }
  },
  computed: {
    isCheer() {
      return Boolean(this.currentNotice && this.currentNotice.variant === 'cheer')
    },
    isPaused() {
      return this.isPageHidden || this.isHovering
    },
    animationStyle() {
      return {
        '--scroll-start': `${this.animationStart}px`,
        '--scroll-duration': `${this.animationDuration}s`,
        animationPlayState: this.isPaused ? 'paused' : 'running'
      }
    }
  },
  mounted() {
    this.isPageHidden = document.hidden
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
    window.removeEventListener('resize', this.handleResize)
    window.clearTimeout(this.resizeTimer)
    window.cancelAnimationFrame(this.animationFrame)
  },
  methods: {
    showNotification(message, glowing = false) {
      const normalizedMessage = String(message || '').trim()
      if (!normalizedMessage) {
        return
      }

      this.messageQueue.push({
        id: this.nextNoticeId++,
        message: normalizedMessage,
        variant: glowing ? 'cheer' : 'notice'
      })
      this.showNextMessage()
    },
    showNextMessage() {
      if (this.isPageHidden || this.isVisible || this.currentNotice || this.messageQueue.length === 0) {
        return
      }

      this.currentNotice = this.messageQueue.shift()
      this.isHovering = false
      this.isVisible = true
      this.prepareAnimation()
    },
    prepareAnimation() {
      this.animationReady = false
      window.cancelAnimationFrame(this.animationFrame)

      this.$nextTick(() => {
        const track = this.$refs.track
        const message = this.$refs.message
        if (!track || !message || !this.currentNotice) {
          return
        }

        const travelDistance = track.clientWidth + message.scrollWidth
        const pixelsPerSecond = this.isCheer ? 104 : 86
        const minimumDuration = this.isCheer ? 10 : 12
        const maximumDuration = this.isCheer ? 30 : 34

        this.animationStart = Math.ceil(track.clientWidth)
        this.animationDuration = Math.min(
          maximumDuration,
          Math.max(minimumDuration, travelDistance / pixelsPerSecond)
        )

        this.animationFrame = window.requestAnimationFrame(() => {
          if (this.isVisible && this.currentNotice) {
            this.animationReady = true
          }
        })
      })
    },
    handleAnimationEnd() {
      this.finishCurrentNotice()
    },
    closeNotification() {
      this.finishCurrentNotice()
    },
    finishCurrentNotice() {
      if (!this.isVisible) {
        return
      }
      this.animationReady = false
      this.isVisible = false
    },
    handleAfterLeave() {
      this.currentNotice = null
      this.showNextMessage()
    },
    handleVisibilityChange() {
      this.isPageHidden = document.hidden
      if (!this.isPageHidden) {
        this.showNextMessage()
      }
    },
    handleResize() {
      window.clearTimeout(this.resizeTimer)
      this.resizeTimer = window.setTimeout(() => {
        if (this.isVisible && this.currentNotice) {
          this.prepareAnimation()
        }
      }, 120)
    }
  }
}
</script>

<style scoped>
@keyframes notice-marquee {
  from {
    transform: translate3d(var(--scroll-start), 0, 0);
  }
  to {
    transform: translate3d(-100%, 0, 0);
  }
}

@keyframes cheer-sweep {
  0%, 12% {
    transform: translate3d(-145%, 0, 0) skewX(-20deg);
  }
  52%, 100% {
    transform: translate3d(420%, 0, 0) skewX(-20deg);
  }
}

@keyframes cheer-frame {
  0%, 100% {
    border-color: #b3d8ff;
    box-shadow: 0 10px 30px rgba(64, 158, 255, 0.16),
      0 0 0 1px rgba(64, 158, 255, 0.04) inset;
  }
  50% {
    border-color: #79bbff;
    box-shadow: 0 12px 36px rgba(64, 158, 255, 0.24),
      0 0 0 3px rgba(64, 158, 255, 0.08) inset;
  }
}

@keyframes cheer-energy {
  from {
    background-position: 0 0;
  }
  to {
    background-position: 240px 0;
  }
}

@keyframes trophy-strike {
  0%, 100% {
    transform: translateY(0) rotate(0deg) scale(1);
    filter: drop-shadow(0 0 0 rgba(230, 162, 60, 0));
  }
  10% {
    transform: translateY(-3px) rotate(-10deg) scale(1.14);
    filter: drop-shadow(0 3px 5px rgba(230, 162, 60, 0.3));
  }
  20% {
    transform: translateY(0) rotate(8deg) scale(1.06);
  }
  32% {
    transform: translateY(0) rotate(0deg) scale(1);
    filter: drop-shadow(0 0 0 rgba(230, 162, 60, 0));
  }
}

@keyframes trophy-ring {
  0%, 30% {
    opacity: 0;
    transform: scale(0.72);
  }
  38% {
    opacity: 0.55;
  }
  68%, 100% {
    opacity: 0;
    transform: scale(1.42);
  }
}

@keyframes celebration-fall {
  0% {
    opacity: 0;
    transform: translate3d(0, -22px, 0) rotate(0deg);
  }
  12% {
    opacity: 0.9;
  }
  100% {
    opacity: 0;
    transform: translate3d(20px, 96px, 0) rotate(280deg);
  }
}

@keyframes celebration-spark {
  0%, 60%, 100% {
    opacity: 0;
    transform: scale(0.25) rotate(0deg);
  }
  70% {
    opacity: 0.95;
    transform: scale(1) rotate(45deg);
  }
  82% {
    opacity: 0;
    transform: scale(1.5) rotate(90deg);
  }
}

@keyframes spotlight-cross-left {
  0%, 100% {
    opacity: 0.16;
    transform: rotate(-24deg) translate3d(-8%, 0, 0) scaleY(0.92);
  }
  50% {
    opacity: 0.44;
    transform: rotate(22deg) translate3d(10%, 0, 0) scaleY(1.08);
  }
}

@keyframes spotlight-cross-right {
  0%, 100% {
    opacity: 0.38;
    transform: rotate(24deg) translate3d(8%, 0, 0) scaleY(1.06);
  }
  50% {
    opacity: 0.14;
    transform: rotate(-22deg) translate3d(-10%, 0, 0) scaleY(0.94);
  }
}

@keyframes reduced-hold {
  from, to {
    opacity: 1;
    transform: none;
  }
}

.notice-bar {
  position: fixed;
  top: 14px;
  left: 50%;
  z-index: 4000;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  width: calc(100vw - 40px);
  max-width: 1080px;
  min-height: 58px;
  padding: 0 12px 0 14px;
  overflow: hidden;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  transform: translateX(-50%);
  font-family: "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.08);
  isolation: isolate;
}

.notice-bar.is-notice {
  color: #303133;
  background: rgba(255, 255, 255, 0.98);
}

.notice-bar.is-notice::before {
  position: absolute;
  top: 10px;
  bottom: 10px;
  left: 0;
  width: 3px;
  background: #409eff;
  content: "";
}

.notice-bar.is-cheer {
  grid-template-columns: auto minmax(0, 1fr) auto;
  max-width: 1180px;
  min-height: 64px;
  color: #303133;
  background: rgba(255, 255, 255, 0.98);
  border-color: #b3d8ff;
  animation: cheer-frame 2.8s ease-in-out infinite;
}

.notice-bar.is-cheer::before {
  position: absolute;
  z-index: 1;
  top: -35%;
  bottom: -35%;
  left: 0;
  width: 28%;
  pointer-events: none;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(64, 158, 255, 0.05),
    rgba(64, 158, 255, 0.2),
    rgba(230, 162, 60, 0.1),
    transparent
  );
  content: "";
  animation: cheer-sweep 3.2s cubic-bezier(0.2, 0.7, 0.2, 1) infinite;
}

.notice-bar.is-cheer::after {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1;
  height: 3px;
  background: repeating-linear-gradient(
    90deg,
    #409eff 0,
    #79bbff 34px,
    #e6a23c 58px,
    #67c23a 74px,
    #409eff 120px
  );
  background-size: 240px 100%;
  content: "";
  animation: cheer-energy 2.2s linear infinite;
}

.notice-identity,
.notice-track,
.notice-close {
  position: relative;
  z-index: 2;
}

.notice-identity {
  display: flex;
  align-items: center;
  padding-right: 4px;
}

.is-cheer .notice-identity {
  padding-right: 6px;
}

.notice-icon {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  color: #409eff;
  background: #ecf5ff;
  border: 1px solid #b3d8ff;
  border-radius: 6px;
  font-size: 17px;
}

.is-cheer .notice-icon {
  position: relative;
  width: 40px;
  height: 40px;
  color: #e6a23c;
  background: #fdf6ec;
  border-color: #f5dab1;
  box-shadow: 0 0 0 3px rgba(230, 162, 60, 0.1),
    0 5px 14px rgba(230, 162, 60, 0.16);
  font-size: 21px;
  animation: trophy-strike 2.8s ease-in-out infinite;
}

.is-cheer .notice-icon::before,
.is-cheer .notice-icon::after {
  position: absolute;
  inset: -5px;
  border: 1px solid rgba(230, 162, 60, 0.5);
  border-radius: 8px;
  content: "";
  pointer-events: none;
  animation: trophy-ring 2.8s ease-out infinite;
}

.is-cheer .notice-icon::after {
  border-color: rgba(64, 158, 255, 0.45);
  animation-delay: 0.18s;
}

.notice-track {
  display: flex;
  align-items: center;
  min-width: 0;
  height: 56px;
  margin: 0 14px 0 10px;
  overflow: hidden;
  mask-image: linear-gradient(90deg, transparent, #000 18px, #000 calc(100% - 18px), transparent);
  -webkit-mask-image: linear-gradient(90deg, transparent, #000 18px, #000 calc(100% - 18px), transparent);
}

.is-cheer .notice-track {
  height: 62px;
  margin-right: 20px;
  margin-left: 14px;
}

.notice-message {
  flex: 0 0 auto;
  opacity: 0;
  color: #606266;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1.5;
  white-space: nowrap;
  will-change: transform;
}

.notice-message.is-running {
  opacity: 1;
  animation: notice-marquee var(--scroll-duration) linear forwards;
}

.is-cheer .notice-message {
  color: #303133;
  font-size: 16px;
  font-weight: 700;
  text-shadow: 0 1px 10px rgba(64, 158, 255, 0.12);
}

.notice-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  margin-left: 8px;
  padding: 0;
  color: #909399;
  background: transparent;
  border: 0;
  border-radius: 6px;
  outline: none;
  cursor: pointer;
  font-size: 16px;
  transition: color 180ms ease, background-color 180ms ease, transform 180ms ease;
}

.notice-close:hover {
  color: #303133;
  background: #f5f7fa;
}

.notice-close:active {
  transform: scale(0.94);
}

.notice-close:focus-visible {
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.2);
}

.is-cheer .notice-close {
  color: #909399;
}

.is-cheer .notice-close:hover {
  color: #409eff;
  background: #ecf5ff;
}

.is-cheer .notice-close:focus-visible {
  box-shadow: 0 0 0 3px rgba(64, 158, 255, 0.2);
}

.celebration-layer {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.celebration-spotlights,
.celebration-confetti,
.celebration-sparks {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.celebration-spotlights {
  opacity: 0.85;
}

.celebration-beam {
  position: absolute;
  top: -94px;
  left: 50%;
  width: 25%;
  height: 250px;
  background: linear-gradient(
    to bottom,
    rgba(64, 158, 255, 0.18),
    rgba(64, 158, 255, 0.08) 42%,
    rgba(64, 158, 255, 0) 88%
  );
  clip-path: polygon(47% 0, 53% 0, 100% 100%, 0 100%);
  filter: blur(1px);
  transform-origin: 50% 0;
  animation: spotlight-cross-left 4.8s ease-in-out infinite;
}

.celebration-beam:nth-child(1) {
  left: -4%;
  animation-delay: -2.2s;
}

.celebration-beam:nth-child(2) {
  left: 12%;
  background: linear-gradient(
    to bottom,
    rgba(230, 162, 60, 0.2),
    rgba(230, 162, 60, 0.08) 44%,
    rgba(230, 162, 60, 0) 88%
  );
  animation: spotlight-cross-right 5.4s ease-in-out -3.8s infinite;
}

.celebration-beam:nth-child(3) {
  left: 28%;
  background: linear-gradient(
    to bottom,
    rgba(103, 194, 58, 0.17),
    rgba(103, 194, 58, 0.07) 44%,
    rgba(103, 194, 58, 0) 88%
  );
  animation-duration: 6.2s;
  animation-delay: -1.4s;
}

.celebration-beam:nth-child(4) {
  left: 43%;
  width: 30%;
  background: linear-gradient(
    to bottom,
    rgba(83, 200, 255, 0.2),
    rgba(83, 200, 255, 0.08) 42%,
    rgba(83, 200, 255, 0) 88%
  );
  animation: spotlight-cross-right 4.4s ease-in-out -2.6s infinite;
}

.celebration-beam:nth-child(5) {
  left: 58%;
  background: linear-gradient(
    to bottom,
    rgba(127, 140, 255, 0.17),
    rgba(127, 140, 255, 0.07) 45%,
    rgba(127, 140, 255, 0) 88%
  );
  animation-duration: 5.7s;
  animation-delay: -4.1s;
}

.celebration-beam:nth-child(6) {
  left: 74%;
  background: linear-gradient(
    to bottom,
    rgba(245, 108, 108, 0.16),
    rgba(245, 108, 108, 0.06) 46%,
    rgba(245, 108, 108, 0) 88%
  );
  animation: spotlight-cross-right 6.4s ease-in-out -1.8s infinite;
}

.celebration-beam:nth-child(7) {
  left: 90%;
  animation-duration: 5.1s;
  animation-delay: -3.2s;
}

.celebration-piece {
  position: absolute;
  top: -18px;
  width: 5px;
  height: 16px;
  background: #409eff;
  opacity: 0;
  animation: celebration-fall 2.9s linear infinite;
}

.celebration-piece:nth-child(2n) {
  width: 8px;
  height: 4px;
  background: #e6a23c;
}

.celebration-piece:nth-child(3n) {
  background: #67c23a;
}

.celebration-piece:nth-child(4n) {
  width: 4px;
  height: 4px;
  border-radius: 50%;
}

.celebration-piece:nth-child(5n) {
  background: #f56c6c;
}

.celebration-piece:nth-child(6n) {
  background: #53c8ff;
}

.celebration-piece:nth-child(7n) {
  background: #7f8cff;
}

.celebration-piece:nth-child(9n) {
  width: 9px;
  height: 3px;
  background: #f08bd8;
}

.celebration-piece:nth-child(1) { left: 5%; animation-delay: -1.2s; }
.celebration-piece:nth-child(2) { left: 13%; animation-delay: -2.4s; }
.celebration-piece:nth-child(3) { left: 23%; animation-delay: -0.5s; }
.celebration-piece:nth-child(4) { left: 34%; animation-delay: -1.9s; }
.celebration-piece:nth-child(5) { left: 43%; animation-delay: -2.7s; }
.celebration-piece:nth-child(6) { left: 52%; animation-delay: -0.9s; }
.celebration-piece:nth-child(7) { left: 61%; animation-delay: -2.1s; }
.celebration-piece:nth-child(8) { left: 69%; animation-delay: -1.5s; }
.celebration-piece:nth-child(9) { left: 77%; animation-delay: -2.6s; }
.celebration-piece:nth-child(10) { left: 84%; animation-delay: -0.3s; }
.celebration-piece:nth-child(11) { left: 91%; animation-delay: -1.7s; }
.celebration-piece:nth-child(12) { left: 96%; animation-delay: -2.2s; }
.celebration-piece:nth-child(13) { left: 9%; animation-delay: -0.2s; }
.celebration-piece:nth-child(14) { left: 18%; animation-delay: -1.6s; }
.celebration-piece:nth-child(15) { left: 38%; animation-delay: -2.2s; }
.celebration-piece:nth-child(16) { left: 57%; animation-delay: -0.6s; }
.celebration-piece:nth-child(17) { left: 73%; animation-delay: -1.1s; }
.celebration-piece:nth-child(18) { left: 88%; animation-delay: -2.5s; }
.celebration-piece:nth-child(19) { left: 3%; animation-delay: -2.8s; animation-duration: 3.5s; }
.celebration-piece:nth-child(20) { left: 16%; animation-delay: -0.7s; animation-duration: 2.4s; }
.celebration-piece:nth-child(21) { left: 26%; animation-delay: -2.1s; animation-duration: 3.2s; }
.celebration-piece:nth-child(22) { left: 31%; animation-delay: -1.2s; animation-duration: 2.6s; }
.celebration-piece:nth-child(23) { left: 41%; animation-delay: -0.3s; animation-duration: 3.7s; }
.celebration-piece:nth-child(24) { left: 47%; animation-delay: -2.6s; animation-duration: 2.8s; }
.celebration-piece:nth-child(25) { left: 55%; animation-delay: -1.5s; animation-duration: 3.4s; }
.celebration-piece:nth-child(26) { left: 63%; animation-delay: -0.8s; animation-duration: 2.5s; }
.celebration-piece:nth-child(27) { left: 71%; animation-delay: -2.9s; animation-duration: 3.1s; }
.celebration-piece:nth-child(28) { left: 79%; animation-delay: -1.3s; animation-duration: 3.6s; }
.celebration-piece:nth-child(29) { left: 86%; animation-delay: -0.4s; animation-duration: 2.7s; }
.celebration-piece:nth-child(30) { left: 94%; animation-delay: -2.3s; animation-duration: 3.3s; }
.celebration-piece:nth-child(31) { left: 7%; animation-delay: -1.8s; animation-duration: 2.5s; }
.celebration-piece:nth-child(32) { left: 21%; animation-delay: -2.7s; animation-duration: 3.5s; }
.celebration-piece:nth-child(33) { left: 36%; animation-delay: -0.9s; animation-duration: 2.9s; }
.celebration-piece:nth-child(34) { left: 59%; animation-delay: -2s; animation-duration: 3.8s; }
.celebration-piece:nth-child(35) { left: 76%; animation-delay: -0.6s; animation-duration: 2.6s; }
.celebration-piece:nth-child(36) { left: 91%; animation-delay: -1.7s; animation-duration: 3.4s; }

.celebration-spark {
  position: absolute;
  width: 10px;
  height: 10px;
  opacity: 0;
  animation: celebration-spark 2.6s ease-in-out infinite;
}

.celebration-spark::before,
.celebration-spark::after {
  position: absolute;
  top: 50%;
  left: 0;
  width: 100%;
  height: 2px;
  background: #e6a23c;
  border-radius: 2px;
  content: "";
  transform: translateY(-50%);
}

.celebration-spark::after {
  transform: translateY(-50%) rotate(90deg);
}

.celebration-spark:nth-child(1) { top: 16%; left: 7%; animation-delay: -0.4s; }
.celebration-spark:nth-child(2) { top: 62%; left: 18%; animation-delay: -1.6s; }
.celebration-spark:nth-child(3) { top: 20%; left: 31%; animation-delay: -2.2s; }
.celebration-spark:nth-child(4) { top: 66%; left: 43%; animation-delay: -0.9s; }
.celebration-spark:nth-child(5) { top: 18%; left: 54%; animation-delay: -1.9s; }
.celebration-spark:nth-child(6) { top: 58%; left: 64%; animation-delay: -1.2s; }
.celebration-spark:nth-child(7) { top: 12%; left: 73%; animation-delay: -2.5s; }
.celebration-spark:nth-child(8) { top: 68%; left: 82%; animation-delay: -0.6s; }
.celebration-spark:nth-child(9) { top: 24%; left: 90%; animation-delay: -1.5s; }
.celebration-spark:nth-child(10) { top: 56%; left: 96%; animation-delay: -2.1s; }

.announcement-drop-enter-active,
.announcement-drop-leave-active {
  transition: opacity 260ms ease, transform 360ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

.announcement-drop-enter,
.announcement-drop-leave-to {
  opacity: 0;
  transform: translate3d(-50%, -16px, 0) scale(0.98);
}

@media (max-width: 720px) {
  .notice-bar,
  .notice-bar.is-cheer {
    top: 8px;
    width: calc(100vw - 16px);
    min-height: 56px;
    padding-right: 8px;
    padding-left: 10px;
  }

  .notice-bar.is-cheer {
    grid-template-columns: auto minmax(0, 1fr) auto;
    min-height: 64px;
  }

  .notice-identity,
  .is-cheer .notice-identity {
    padding-right: 10px;
  }

  .notice-icon,
  .is-cheer .notice-icon {
    width: 36px;
    height: 36px;
    margin-right: 0;
    font-size: 18px;
  }

  .notice-track,
  .is-cheer .notice-track {
    height: 54px;
    margin-right: 8px;
    margin-left: 10px;
  }

  .is-cheer .notice-message {
    font-size: 16px;
  }

  .notice-close {
    width: 32px;
    height: 32px;
    margin-left: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .notice-message.is-running {
    transform: none;
    animation-name: reduced-hold;
    animation-duration: 8s;
  }

  .notice-bar.is-cheer::before,
  .notice-bar.is-cheer::after,
  .notice-bar.is-cheer,
  .is-cheer .notice-icon,
  .is-cheer .notice-icon::before,
  .is-cheer .notice-icon::after,
  .celebration-beam,
  .celebration-piece,
  .celebration-spark {
    animation: none;
  }

  .announcement-drop-enter-active,
  .announcement-drop-leave-active {
    transition-duration: 1ms;
  }
}
</style>
